import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { ArrowLeftRight, TrendingUp, Users } from 'lucide-react';

// Public preview page for a shared DCA template — anyone with the link lands here,
// and one-tap "Adopt" copies the template into the current user's custom templates.
export default function SharedTemplate() {
  const { share_id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [shared, setShared] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [adopting, setAdopting] = useState(false);

  useEffect(() => {
    api.get(`/wallet/recurring_conversions/shared/${share_id}`)
      .then(r => setShared(r.data))
      .catch(() => setNotFound(true));
  }, [share_id]);

  const adopt = async () => {
    if (!user) { nav(`/login?next=/convert/shared/${share_id}`); return; }
    setAdopting(true);
    try {
      await api.post(`/wallet/recurring_conversions/adopt/${share_id}`);
      toast.success('Template adopted — find it under Convert → Recurring');
      nav('/convert');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to adopt'); }
    finally { setAdopting(false); }
  };

  if (notFound) {
    return (
      <div className="max-w-lg mx-auto py-24 text-center" data-testid="shared-template-notfound">
        <div className="text-white text-xl font-semibold mb-2">Shared template not found</div>
        <div className="text-[#848E9C] text-sm mb-6">The link may be invalid or expired.</div>
        <Link to="/convert" className="inline-block px-5 py-2 bg-[#FCD535] text-[#0B0E11] rounded-sm font-semibold">Explore templates</Link>
      </div>
    );
  }
  if (!shared) {
    return <div className="max-w-lg mx-auto py-24 text-center text-[#848E9C]" data-testid="shared-template-loading">Loading template…</div>;
  }
  return (
    <div className="max-w-xl mx-auto px-4 py-12" data-testid="shared-template-page">
      <div className="border border-[#2B3139] bg-[#181A20] p-6 rounded-sm">
        <div className="flex items-center gap-1 text-[10px] uppercase font-mono text-[#FCD535] mb-2">
          <Users className="w-3 h-3" /> Shared DCA template
        </div>
        <h1 className="font-display text-white text-3xl font-bold" data-testid="shared-template-label">{shared.label}</h1>
        <div className="text-[#848E9C] text-sm mt-1">
          Shared by <span className="text-white font-semibold" data-testid="shared-template-owner">@{shared.owner_name}</span>
          {shared.adopts > 0 && <> · <span className="text-[#0ECB81]">{shared.adopts} {shared.adopts === 1 ? 'person has' : 'people have'} adopted this</span></>}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
          <div className="border border-[#2B3139] bg-[#0B0E11] p-3">
            <div className="text-[10px] uppercase font-mono text-[#848E9C]">Frequency</div>
            <div className="text-white font-mono text-lg mt-0.5 uppercase" data-testid="shared-template-interval">{shared.interval}</div>
          </div>
          <div className="border border-[#2B3139] bg-[#0B0E11] p-3">
            <div className="text-[10px] uppercase font-mono text-[#848E9C]">Amount</div>
            <div className="text-white font-mono text-lg mt-0.5" data-testid="shared-template-amount">
              {shared.default_amount} {shared.from_symbol}
            </div>
          </div>
        </div>

        <div className="border border-[#2B3139] bg-[#0B0E11] p-3 mt-3">
          <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-2">Allocation</div>
          {shared.splits.map(s => (
            <div key={s.to_symbol} className="flex items-center justify-between py-1" data-testid={`shared-split-${s.to_symbol}`}>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-3 h-3 text-[#FCD535]" />
                <span className="text-white font-semibold">{s.to_symbol}</span>
              </div>
              <div className="flex items-center gap-3 flex-1 max-w-xs ml-4">
                <div className="flex-1 h-1.5 bg-[#2B3139] overflow-hidden">
                  <div className="h-full bg-[#FCD535]" style={{ width: `${Math.round(s.weight * 100)}%` }} />
                </div>
                <span className="text-[#B7BDC6] font-mono text-xs w-10 text-right">{Math.round(s.weight * 100)}%</span>
              </div>
            </div>
          ))}
        </div>

        <div className="text-[11px] text-[#848E9C] mt-3">
          Adopting adds this as a personal template — nothing is executed until you run it manually or its next scheduled trigger fires.
        </div>

        <Button
          onClick={adopt}
          disabled={adopting}
          className="w-full h-12 mt-5 bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold text-base"
          data-testid="shared-template-adopt"
        >
          <ArrowLeftRight className="w-4 h-4 mr-2" /> {user ? (adopting ? 'Adopting…' : 'Adopt this template') : 'Log in to adopt'}
        </Button>
      </div>
    </div>
  );
}
